/**
 * Pure geometry for a portal-positioned popover.
 *
 * A popover that lives inside a scrolling drawer (the filter cajón) has
 * to escape its clipping ancestor: it renders to ``document.body`` with
 * ``position: fixed`` and is placed by hand against its trigger. The
 * placement MATH is the bug-prone part — flip when it would overflow
 * the viewport, clamp sideways at the edges, cap the height so a tall
 * panel scrolls instead of running off-screen.
 *
 * That math lives HERE as a pure function, exhaustively unit-tested,
 * precisely because jsdom reports every ``getBoundingClientRect`` as
 * zero: the React wrapper's positioning can't be verified in vitest, so
 * the geometry is separated out where it can. Same measure→place→flip
 * pattern proven by ``bi-components``'s ``InfoTip``, generalised.
 *
 * All inputs and outputs are viewport-relative CSS pixels (what
 * ``getBoundingClientRect`` returns and what ``position: fixed`` reads).
 */

export interface Rect {
  left: number;
  top: number;
  width: number;
  height: number;
}

export interface Viewport {
  width: number;
  height: number;
}

export interface PopoverPlacementOptions {
  /** Gap between the trigger and the panel. Default 6. */
  gap?: number;
  /** Minimum distance kept from every viewport edge. Default 8. */
  margin?: number;
  /**
   * Horizontal alignment of the panel to the anchor when there is room:
   * - ``"start"`` (default) — panel's left edge aligns to the anchor's;
   * - ``"center"`` — panel is centred on the anchor.
   * Either way the result is clamped inside the viewport.
   */
  align?: "start" | "center";
  /**
   * The panel's natural width. When the panel should at least match the
   * trigger (a dropdown), pass the trigger width as ``minWidth`` and the
   * caller sets ``width`` on the element; this function only positions.
   */
  panel: { width: number; height: number };
}

export interface PopoverPosition {
  left: number;
  top: number;
  /** Which side of the anchor the panel ended up on. */
  placement: "below" | "above";
  /**
   * Height budget for the panel so it never runs past the viewport
   * margin. A panel taller than this must scroll internally. Always
   * ``>= 0``.
   */
  maxHeight: number;
}

function clamp(value: number, lo: number, hi: number): number {
  // Guard the degenerate case (panel wider/taller than the viewport):
  // keep the low edge rather than letting ``hi < lo`` invert the clamp.
  if (hi < lo) return lo;
  return Math.min(Math.max(value, lo), hi);
}

/**
 * Place a popover panel against its trigger.
 *
 * Prefers below the anchor; flips above when below would overflow the
 * viewport AND above has more room. Clamps the left edge inside the
 * margins with the alignment preference, and caps the height to the
 * space available on the chosen side.
 */
export function computePopoverPosition(
  anchor: Rect,
  viewport: Viewport,
  opts: PopoverPlacementOptions,
): PopoverPosition {
  const gap = opts.gap ?? 6;
  const margin = opts.margin ?? 8;
  const align = opts.align ?? "start";
  const { width: panelW, height: panelH } = opts.panel;

  const spaceBelow = viewport.height - anchor.top - anchor.height - gap - margin;
  const spaceAbove = anchor.top - gap - margin;

  // Below unless it does not fit AND above has strictly more room —
  // ties keep the natural downward placement.
  const fitsBelow = panelH <= spaceBelow;
  const placement: "below" | "above" =
    fitsBelow || spaceBelow >= spaceAbove ? "below" : "above";

  const maxHeight = Math.max(
    0,
    placement === "below" ? spaceBelow : spaceAbove,
  );

  const top =
    placement === "below"
      ? anchor.top + anchor.height + gap
      : // Anchor the panel's BOTTOM at the gap above the trigger; when
        // capped, its top rides the margin.
        Math.max(margin, anchor.top - gap - Math.min(panelH, maxHeight));

  const rawLeft =
    align === "center"
      ? anchor.left + anchor.width / 2 - panelW / 2
      : anchor.left;
  const left = clamp(
    rawLeft,
    margin,
    viewport.width - margin - panelW,
  );

  return {
    left: Math.round(left),
    top: Math.round(top),
    placement,
    maxHeight: Math.round(maxHeight),
  };
}
